import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { get, truncate } from 'lodash';
import {
  Box,
  Link,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import * as routes from 'router/routes';
import { DASHBOARD_DATA_KEYS, NO_DATA_VALUE_FOR_SORTING } from 'constants/dashboard';
import { SORT_OPTIONS } from 'constants/common';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { TitleTooltip } from 'components/simple';
import { ArrowSortDoubleIcon, ArrowSortIcon } from 'assets/icons';

const StyledCell = styled(TableCell)({
  height: 70,
  border: 'none',
});

const styles = {
  sortLabel: ({ palette }) => ({
    flexDirection: 'row-reverse',
    color: `${palette.text.primary} !important`,

    '.MuiTableSortLabel-icon': {
      width: 16,
      height: 16,
      flex: 'none',
      color: `${palette.text.primary} !important`,
      opacity: '1 !important',
    },
  }),
  zeroValue: ({ palette }) => ({
    color: palette.colors.lightGrey,
  }),
  valueBox: (backgroundColor) => ({
    display: 'block',
    flex: 'none',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor,
  }),
  headerCell: ({ palette }) => ({
    borderBottom: `1px solid ${palette.colors.darkPale}`,
  }),
  table: { tableLayout: 'fixed' },
};

const headerCells = [
  {
    label: 'Project Name',
    sortable: false,
    align: 'left',
    dataKey: DASHBOARD_DATA_KEYS.PROJECT_NAME,
  },
  {
    label: 'Score',
    sortable: true,
    cellWidth: '11%',
    align: 'center',
    dataKey: DASHBOARD_DATA_KEYS.PERFORMANCE,
  },
  {
    label: 'Compared to Previous',
    sortable: true,
    cellWidth: '13%',
    align: 'center',
    dataKey: DASHBOARD_DATA_KEYS.GROWING,
  },
  {
    label: 'Participation',
    sortable: true,
    cellWidth: '15%',
    align: 'center',
    dataKey: DASHBOARD_DATA_KEYS.PARTICIPATION,
  },
  {
    label: 'No. Surveys',
    sortable: true,
    cellWidth: '14%',
    align: 'center',
    dataKey: DASHBOARD_DATA_KEYS.SURVEYS_NUMBER,
  },
];

const orderedDataKeys = [
  DASHBOARD_DATA_KEYS.PERFORMANCE,
  DASHBOARD_DATA_KEYS.GROWING,
  DASHBOARD_DATA_KEYS.PARTICIPATION,
];

const renderProjectDataByKey = (projectData, key) => {
  const { value, color } = get(projectData, [key], {});

  return value !== NO_DATA_VALUE_FOR_SORTING ? (
    <Box display="flex" justifyContent="center" alignItems="center" columnGap={1}>
      <Box component="span" sx={styles.valueBox(color)} />
      <Typography
        variant="subtitle1"
        component="span"
        sx={[value === 0 && key !== DASHBOARD_DATA_KEYS.GROWING && styles.zeroValue]}
      >
        {`${value}%`}
      </Typography>
    </Box>
  ) : (
    <Typography variant="subtitle1" component="span" sx={styles.zeroValue}>
      No data
    </Typography>
  );
};

export const DashboardPortfolioTable = ({
  tableData,
  rowsOrder,
  rowsOrderBy,
  handleChangeTableSort,
  organisationId,
}) => {
  const { getResponsiveValueForCurrent } = useBreakpointFlags();
  const titleLength = getResponsiveValueForCurrent({
    xs: 30,
    sm: 30,
    md: 30,
    lg: 35,
    xl: 35,
    xxl: 40,
  });

  return (
    <Table sx={styles.table}>
      <TableHead>
        <TableRow>
          {headerCells.map(({ label, cellWidth, align, dataKey, sortable }) => {
            const isSorted = rowsOrderBy === dataKey;

            return (
              <StyledCell key={dataKey} width={cellWidth} align={align} sx={styles.headerCell}>
                {sortable ? (
                  <TableSortLabel
                    active={isSorted}
                    direction={isSorted ? rowsOrder : SORT_OPTIONS.DESC}
                    IconComponent={isSorted ? ArrowSortIcon : ArrowSortDoubleIcon}
                    onClick={() => handleChangeTableSort(dataKey)}
                    sx={styles.sortLabel}
                  >
                    <Typography variant={isSorted ? 'button' : 'body1'} component="span" textAlign="center">
                      {label}
                    </Typography>
                  </TableSortLabel>
                ) : (
                  <Typography variant="subtitle1" component="span">
                    {label}
                  </Typography>
                )}
              </StyledCell>
            );
          })}
        </TableRow>
      </TableHead>

      <TableBody>
        {tableData.map((projectData) => (
          <TableRow key={projectData.id}>
            <StyledCell>
              <TitleTooltip titleLength={titleLength} title={projectData.projectName}>
                <Link
                  component={RouterLink}
                  to={{
                    pathname: `/${organisationId}/${routes.dashboard}/${projectData.id}`,
                    state: { backTo: `${routes.dashboardPortfolio}/${organisationId}` },
                  }}
                  variant="subtitle1-bold"
                  underline="none"
                >
                  {truncate(projectData.projectName, { length: titleLength })}
                </Link>
              </TitleTooltip>
            </StyledCell>

            {orderedDataKeys.map((dataKey) => (
              <StyledCell key={dataKey} align="center">
                {renderProjectDataByKey(projectData, dataKey)}
              </StyledCell>
            ))}

            <StyledCell align="center">
              <Typography variant="subtitle1" component="span" sx={!projectData.surveysNumber ? styles.zeroValue : {}}>
                {projectData.surveysNumber || 0}
              </Typography>
            </StyledCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const dataPropTypes = PropTypes.shape({
  value: PropTypes.number,
  color: PropTypes.string,
}).isRequired;

DashboardPortfolioTable.propTypes = {
  tableData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      projectName: PropTypes.string.isRequired,
      surveysNumber: PropTypes.number.isRequired,
      performance: dataPropTypes,
      growing: dataPropTypes,
      participation: dataPropTypes,
    }),
  ).isRequired,
  rowsOrder: PropTypes.oneOf(Object.values(SORT_OPTIONS)).isRequired,
  rowsOrderBy: PropTypes.oneOf(Object.values(DASHBOARD_DATA_KEYS)).isRequired,
  handleChangeTableSort: PropTypes.func.isRequired,
  organisationId: PropTypes.string.isRequired,
};
